import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Layers, ShieldCheck, Palette, GitBranch, Gift, Webhook, Sparkles, Check, Plus, Minus, Mail, MessageCircle } from "lucide-react";
import Logo from "@/components/Logo";

const pillars = [
  { icon: Layers, title: "Onboarding Portal", desc: "Passwordless magic-link client dashboards, fully white-labeled.", span: "md:col-span-5" },
  { icon: ShieldCheck, title: "Contracts", desc: "Form-to-PDF with type-to-sign. Presigned, time-limited download links.", span: "md:col-span-4" },
  { icon: Palette, title: "Brand Hub", desc: "Logos, hex codes, typography — one durable home for every client.", span: "md:col-span-3" },
  { icon: GitBranch, title: "Roadmap", desc: "Kanban board with boolean client approvals — no guesswork.", span: "md:col-span-4" },
  { icon: Gift, title: "Referrals", desc: "Trackable slugs. Deterministic click + conversion counters.", span: "md:col-span-4" },
  { icon: Webhook, title: "Webhooks", desc: "contract.signed, client.onboarded, referral.converted. Wire Zapier / Make.", span: "md:col-span-4" },
];

const tiers = [
  {
    key: "Tier1",
    label: "Starter",
    price: "€49",
    subtitle: "For solo operators",
    features: ["Up to 10 active clients", "Unlimited contracts", "Brand Hub", "Magic-link client portals", "Outbound webhooks", "BYO-SMTP email"],
    highlighted: false,
  },
  {
    key: "Tier2",
    label: "Growth",
    price: "€129",
    subtitle: "For boutique agencies",
    features: ["Up to 30 active clients", "Custom domain", "White-label portal", "Priority email support", "All Starter features"],
    highlighted: true,
  },
  {
    key: "Tier3",
    label: "Scale",
    price: "€229",
    subtitle: "For growing agencies",
    features: ["Up to 60 active clients", "Advanced webhooks", "Team seats (soon)", "Dedicated Slack channel", "All Growth features"],
    highlighted: false,
  },
  {
    key: "Tier4",
    label: "Agency",
    price: "€399",
    subtitle: "For serious agencies",
    features: ["Unlimited active clients", "Migration help included", "Priority onboarding", "Custom integrations", "All Scale features"],
    highlighted: false,
  },
];

const faqs = [
  {
    q: "Is Foliosy really 100% deterministic with zero AI?",
    a: "Yes. Every feature is built with standard CRUD, relational queries, and event triggers. No machine learning, no probabilistic outputs, no hallucinations. What you see is exactly what you get — and it stays that way.",
  },
  {
    q: "How does the magic-link authentication work?",
    a: "When you add a client, we generate a cryptographically secure single-use token, hash it with SHA-256, email them a unique link, and expire it after 30 minutes. No passwords for clients to forget, no friction.",
  },
  {
    q: "Can I connect my own email (SMTP) provider?",
    a: "Absolutely — that's the Bring-Your-Own-Key model. Paste your SMTP credentials in Settings; we encrypt them with Fernet (AES-128) before storing. All magic links and contract notifications go through your domain.",
  },
  {
    q: "Is Foliosy really a one-time payment or annual subscription?",
    a: "Foliosy is an annual subscription — simple and predictable (€49–€399/yr depending on tier). Separately, we participate in AppSumo with a limited Lifetime Deal — if you have an AppSumo code, apply it at checkout to unlock lifetime access to a specific tier without annual billing.",
  },
  {
    q: "Do signed contracts generate real PDFs?",
    a: "Yes. When your client types their name to sign, we generate a deterministic PDF with their signature, IP, and timestamp. PDFs are served through time-limited presigned URLs so links never leak.",
  },
  {
    q: "Can I white-label the client portal completely?",
    a: "That's the whole point. Upload your logo, set your primary color, write your welcome checklist. Your clients see only your brand. You can even wire your own custom domain like portal.youragency.com.",
  },
  {
    q: "What happens if I need to cancel?",
    a: "Request cancellation from your Subscription tab. We'll reach out within 48 hours. Your data is retained for 30 days in case you change your mind, then permanently deleted. No dark patterns.",
  },
  {
    q: "Does Foliosy integrate with Zapier / Make / n8n?",
    a: "Yes, via outbound webhooks. We fire deterministic JSON payloads on contract.signed, client.onboarded, and referral.converted — wire them into any automation platform that accepts a webhook URL.",
  },
];

function FAQItem({ q, a, open, onToggle, idx }) {
  return (
    <div className="border-b border-slate-200 last:border-0" data-testid={`faq-item-${idx}`}>
      <button onClick={onToggle} className="w-full py-5 flex items-start justify-between gap-4 text-left hover:bg-slate-50/50 -mx-4 px-4 rounded-xl transition-colors">
        <span className="font-semibold text-slate-900 text-lg">{q}</span>
        {open ? <Minus className="w-5 h-5 shrink-0 mt-1 text-slate-500" /> : <Plus className="w-5 h-5 shrink-0 mt-1 text-slate-500" />}
      </button>
      {open && <p className="text-slate-600 pb-5 pr-10 leading-relaxed animate-fade-up">{a}</p>}
    </div>
  );
}

export default function Landing() {
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <div className="min-h-screen hero-gradient">
      <nav className="glass sticky top-4 mx-4 md:mx-12 rounded-full px-6 py-3 flex items-center justify-between z-50">
        <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
          <Logo size={32} />
          <span className="font-bold tracking-tight text-slate-900">Foliosy</span>
        </Link>
        <div className="hidden md:flex items-center gap-6 text-sm text-slate-600">
          <a href="#pricing" className="hover:text-slate-900" data-testid="nav-pricing">Pricing</a>
          <a href="#faq" className="hover:text-slate-900" data-testid="nav-faq">FAQ</a>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/login" className="hidden sm:block text-sm text-slate-600 hover:text-slate-900 px-4 py-2" data-testid="nav-login">Sign in</Link>
          <Link to="/register" className="bg-slate-900 text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-slate-700 active:scale-95" data-testid="nav-register">Start free</Link>
        </div>
      </nav>

      {/* HERO */}
      <section className="px-4 md:px-12 pt-20 pb-24 max-w-7xl mx-auto">
        <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-1.5 text-xs font-medium text-slate-600 mb-8" data-testid="hero-badge">
          <Sparkles className="w-3.5 h-3.5" /> Zero-AI. 100% deterministic. Built for agencies.
        </div>
        <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-slate-900 max-w-4xl leading-[1.05]" data-testid="hero-heading">
          Your Agency. Your Folio.
        </h1>
        <p className="mt-8 text-xl text-slate-600 max-w-2xl">
          White-label client portals. Dynamic contracts. Brand hubs. Roadmaps. Referrals. Webhooks. Every client, beautifully in one place — with zero AI and zero surprises.
        </p>
        <div className="mt-10 flex flex-wrap gap-4">
          <Link to="/register" className="bg-slate-900 text-white rounded-full px-7 py-3.5 font-medium hover:bg-slate-700 active:scale-95 inline-flex items-center gap-2" data-testid="hero-cta-start">
            Create your agency <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/login" className="bg-white border border-slate-200 text-slate-900 rounded-full px-7 py-3.5 font-medium hover:bg-slate-100 active:scale-95" data-testid="hero-cta-login">
            Agency login
          </Link>
        </div>

        <div className="mt-24 grid grid-cols-1 md:grid-cols-12 gap-6" data-testid="pillars-grid">
          {pillars.map((p, i) => (
            <div key={p.title} className={`bento p-8 ${p.span} animate-fade-up`} style={{ animationDelay: `${i * 80}ms` }} data-testid={`pillar-${p.title.toLowerCase().replace(/\s+/g, "-")}`}>
              <p.icon className="w-6 h-6 text-slate-900 mb-4" />
              <h3 className="text-2xl font-bold tracking-tight text-slate-900 mb-2">{p.title}</h3>
              <p className="text-slate-600 leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="px-4 md:px-12 py-24 max-w-7xl mx-auto" data-testid="pricing-section">
        <div className="text-center mb-16">
          <p className="eyebrow mb-3">Annual Pricing</p>
          <h2 className="text-4xl md:text-6xl font-bold tracking-tight text-slate-900 mb-4">Simple, honest pricing.</h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">Four tiers. Annual billing. Upgrade anytime.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6" data-testid="pricing-grid">
          {tiers.map((t) => (
            <div
              key={t.key}
              className={`bento p-8 relative ${t.highlighted ? "border-slate-900 border-2 shadow-xl" : ""}`}
              data-testid={`pricing-${t.key}`}
            >
              {t.highlighted && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-xs px-4 py-1 rounded-full font-bold tracking-wider">
                  MOST POPULAR
                </span>
              )}
              <p className="eyebrow mb-2">{t.subtitle}</p>
              <h3 className="text-3xl font-bold tracking-tight mb-2">{t.label}</h3>
              <p className="text-5xl font-bold mb-1">{t.price}</p>
              <p className="text-sm text-slate-500 mb-8">/year · billed annually</p>
              <ul className="space-y-3 mb-8">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-slate-700">
                    <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Link to="/register" className={`block text-center rounded-full py-3 font-medium ${t.highlighted ? "bg-slate-900 text-white hover:bg-slate-700" : "bg-slate-100 text-slate-900 hover:bg-slate-200"} active:scale-95`} data-testid={`pricing-cta-${t.key}`}>
                Get {t.label}
              </Link>
            </div>
          ))}
        </div>
        <p className="text-center text-slate-500 text-sm mt-8">💳 Secured by Stripe & PayPal · 🎫 Got an AppSumo code? Apply it at checkout for lifetime access · 🔒 Cancel anytime</p>
      </section>

      {/* CTA BLOCK */}
      <section className="px-4 md:px-12 pb-24 max-w-7xl mx-auto">
        <div className="bento p-12 grain-overlay bg-slate-900 text-white" data-testid="cta-block">
          <p className="eyebrow text-slate-400 mb-4">Resell as your own</p>
          <h2 className="text-4xl md:text-5xl font-bold mb-4">Sell it as your own.</h2>
          <p className="text-slate-300 max-w-xl mb-8">
            Bring your own SMTP. Bring your own domain. Your logo, your colors — your clients never leave your brand.
          </p>
          <Link to="/register" className="inline-flex items-center gap-2 bg-white text-slate-900 rounded-full px-7 py-3.5 font-medium hover:bg-slate-100 active:scale-95" data-testid="cta-register">
            Start building <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="px-4 md:px-12 py-24 max-w-4xl mx-auto" data-testid="faq-section">
        <div className="text-center mb-12">
          <p className="eyebrow mb-3">Answers</p>
          <h2 className="text-4xl md:text-6xl font-bold tracking-tight text-slate-900 mb-4">Frequently asked.</h2>
        </div>
        <div className="bento p-6 md:p-10" data-testid="faq-list">
          {faqs.map((f, i) => (
            <FAQItem key={f.q} {...f} idx={i} open={openFaq === i} onToggle={() => setOpenFaq(openFaq === i ? -1 : i)} />
          ))}
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-slate-200 bg-white" data-testid="site-footer">
        <div className="px-4 md:px-12 py-14 max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-10">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Logo size={36} />
              <span className="font-bold tracking-tight text-slate-900 text-lg">Foliosy</span>
            </div>
            <p className="text-sm text-slate-600 max-w-xs">Your Agency. Your Folio. The deterministic operating system for modern agencies.</p>
          </div>
          <div>
            <p className="eyebrow mb-4">Product</p>
            <ul className="space-y-2 text-sm text-slate-600">
              <li><a href="#pricing" className="hover:text-slate-900">Pricing</a></li>
              <li><a href="#faq" className="hover:text-slate-900">FAQ</a></li>
              <li><Link to="/login" className="hover:text-slate-900">Agency login</Link></li>
              <li><Link to="/register" className="hover:text-slate-900">Create agency</Link></li>
            </ul>
          </div>
          <div data-testid="footer-contact">
            <p className="eyebrow mb-4">Contact</p>
            <ul className="space-y-3 text-sm text-slate-700">
              <li>
                <a href="mailto:ciao@foliosy.com" className="inline-flex items-center gap-2 hover:text-slate-900" data-testid="footer-email">
                  <Mail className="w-4 h-4 text-slate-400" /> ciao@foliosy.com
                </a>
              </li>
              <li>
                <a href="https://wa.me/393512182553" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 hover:text-slate-900" data-testid="footer-whatsapp">
                  <MessageCircle className="w-4 h-4 text-slate-400" /> WhatsApp: +39 351 21 82 553
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-200 px-4 md:px-12 py-6 max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-4 text-sm text-slate-500">
          <span>© 2026 Foliosy. All rights reserved.</span>
          <span data-testid="footer-credit" className="font-medium">
            Designed by <a href="https://wa.me/393512182553" target="_blank" rel="noopener noreferrer" className="text-slate-900 hover:underline">ANTRU</a>
          </span>
        </div>
      </footer>
    </div>
  );
}
